import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Stack,
  IconButton,
  CircularProgress,
  Alert,
  Button,
  Grid,
  Avatar,
  Chip,
  Divider
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ListAlt as MatchesIcon,
  Schedule as ScheduleIcon,
  EmojiEvents as TrophyIcon,
  CheckCircle as ApprovedIcon,
  Cancel as RejectedIcon,
  WatchLater as PendingIcon
} from '@mui/icons-material';
import tournamentService from '../services/tournamentService';
import MatchCard from '../components/MatchCard';

// Mock Match Data (ensure structure matches what MatchCard expects)
const mockMatches = [
  {
    id: 'm1',
    match_date: '2024-07-28T10:00:00',
    approval_status: 'approved',
    winning_team: 'team1',
    match_mode: 'Ranked',
    tier: 'Arena',
    team1: {
      score: 2,
      players: [{ user_id: 101, approval_status: 'approved' }],
      decks: [{ deck_id: 'd1', deck_name: 'Inka Rush', deck_raza: 'Inka' }]
    },
    team2: {
      score: 0,
      players: [{ user_id: 102, approval_status: 'approved' }],
      decks: [{ deck_id: 'd2', deck_name: 'Faraones', deck_raza: 'Egipcia' }]
    },
  },
  {
    id: 'm2',
    match_date: '2024-07-28T11:00:00',
    approval_status: 'approved',
    winning_team: 'team1',
    match_mode: 'Amistoso',
    tier: 'Amistoso',
    team1: {
      score: 2,
      players: [{ user_id: 104, approval_status: 'approved' }],
      decks: [{ deck_id: 'd3', deck_name: 'Zeus Control', deck_raza: 'Griega' }]
    },
    team2: {
      score: 1,
      players: [{ user_id: 103, approval_status: 'approved' }],
      decks: [{ deck_id: 'd4', deck_name: 'Vikingos', deck_raza: 'Nórdica' }]
    },
  },
  {
    id: 'm3',
    match_date: '2024-07-29T14:00:00',
    approval_status: 'pending',
    winning_team: null,
    match_mode: 'Torneo',
    tier: 'Torneo',
    team1: {
      score: 0,
      players: [{ user_id: 101, approval_status: 'approved' }],
      decks: [{ deck_id: 'd1', deck_name: 'Inka Rush', deck_raza: 'Inka' }]
    },
    team2: {
      score: 0,
      players: [{ user_id: 104, approval_status: 'pending' }],
      decks: [{ deck_id: 'd3', deck_name: 'Zeus Control', deck_raza: 'Griega' }]
    },
  },
    {
    id: 'm4',
    match_date: '2024-07-29T15:00:00',
    approval_status: 'rejected',
    winning_team: null,
    match_mode: 'Amistoso',
    tier: 'Amistoso',
    team1: {
      score: 1,
      players: [{ user_id: 102, approval_status: 'approved' }],
      decks: [{ deck_id: 'd2', deck_name: 'Faraones', deck_raza: 'Egipcia' }]
    },
    team2: {
      score: 2,
      players: [{ user_id: 105, approval_status: 'rejected' }],
      decks: [{ deck_id: 'd5', deck_name: 'Samurai', deck_raza: 'Japonesa' }]
    },
  },
];

// Mock User Data (needed for MatchCard)
const mockUsersMap = {
  101: "AnaLegend",
  102: "CarlosGamer",
  103: "ElenaProPlayer",
  104: "DavidCardMaster",
  105: "SofíaChampion",
};

const TournamentMatchesPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tournament, setTournament] = useState(null);
  const [matches, setMatches] = useState([]);
  const [usersMap, setUsersMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTournamentDetails = async () => {
      try {
        setLoading(true);
        // Fetch basic tournament info (or use mock)
        const mockTournamentData = {
          id: id,
          name: `Torneo de Prueba ${id}`,
        };
        setTournament(mockTournamentData);
        // const data = await tournamentService.getTournamentById(id);
        // setTournament(data);

        // Set mock matches data
        setMatches(mockMatches);

        // Set mock users map
        setUsersMap(mockUsersMap);

      } catch (error) {
        console.error('Error fetching tournament details:', error);
        setError('Error al cargar los detalles del torneo');
      } finally {
        setLoading(false);
      }
    };

    fetchTournamentDetails();
  }, [id]);

  const handleBack = () => {
    navigate(`/tournaments/${id}`);
  };

  // Handle click within MatchCard
  const handleMatchClick = (event, matchData) => {
    navigate(`/match/${matchData.id}`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !tournament) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '80vh', gap: 2 }}>
        <Alert severity="error">{error || 'No se encontraron datos del torneo'}</Alert>
        <Button variant="contained" onClick={() => navigate(`/tournaments/${id}`)} startIcon={<ArrowBackIcon />}>
          Volver al Torneo
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 1 }, display: 'flex', flexDirection: 'column', alignItems: 'center', width: '90%', maxWidth: '1000px', mx: 'auto', bgcolor: 'transparent' }}>
      <Stack spacing={2} sx={{ width: '100%', bgcolor: 'transparent' }}>
        {/* Header */}
        <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <IconButton onClick={handleBack} sx={{ width: 36, height: 36, bgcolor: 'action.hover', color: 'text.primary', '&:hover': { bgcolor: 'primary.main', color: 'white', transform: 'scale(1.1)' }, transition: 'all 0.2s ease-in-out' }}>
                <ArrowBackIcon sx={{ fontSize: 20 }} />
              </IconButton>
              <Box>
                <Typography variant="h6" fontWeight="bold">Partidas del Torneo</Typography>
                <Typography variant="body2" color="text.secondary">{tournament.name}</Typography>
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* Matches List using MatchCard */}
        {matches.length > 0 ? (
          <Stack spacing={2}>
            {matches.map((match) => (
              <MatchCard 
                key={match.id} 
                match={match} 
                usersMap={usersMap} 
                onClick={handleMatchClick}
              />
            ))}
          </Stack>
        ) : (
          <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
            <MatchesIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" gutterBottom>No hay partidas</Typography>
            <Typography color="text.secondary">Aún no se han generado partidas para este torneo.</Typography>
          </Paper>
        )}

        {/* Back Button */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button variant="outlined" onClick={handleBack} startIcon={<ArrowBackIcon />} sx={{ borderRadius: 1.5, textTransform: 'none', borderColor: '#c89e38', color: '#c89e38', '&:hover': { borderColor: '#b38d2d', bgcolor: 'rgba(200, 158, 56, 0.04)' } }}>
            Volver al Torneo
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default TournamentMatchesPage; 